import "./DCAsset.css";
import { DCAssetPreview } from "./DCAssetPreview";
import { Button } from "../../../ds2/components";
import { AgentResponseMessage } from "winslow-core";
import {
  FileText,
  File,
  Image,
  Video,
  Music,
  Archive,
  Download,
  Eye,
  EyeOff,
  Code,
  FileCode,
  Maximize2,
  Pin,
  PinOff,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLastViewedMessageId, selectUser } from "../../../redux/features/appData";
import { apiToggleMessagePin } from "../../../services/AIService";
import { updateSpecificMessage } from "../../../redux/features/agent/agentSlice";
import toast from "react-hot-toast";
import { FilePreview, isFileViewableInBrowser } from "../../shared/FilePreview";
import { DCBaseProps } from "./DCBase";

type DCAssetProps = DCBaseProps & {
  data: {
    title: string;
    fileName: string;
    name: string;
    cloudURL: string;
  }
  preview?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
};

export const DCAsset = ({
  message,
  data,
  preview = false,
  onCancel,
  onSubmit,
}: DCAssetProps) => {

  
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storeUser = useSelector(selectUser);

  const title = data?.title || data?.fileName || data?.name;
  const url = data?.cloudURL?.trim()

  const fileExtension = url?.split(".").pop()?.toLowerCase();

  // Determine file type and get appropriate icon
  const getFileTypeInfo = () => {
    if (!fileExtension)
      return {
        icon: <File className="file-icon generic" size={24} />,
        type: "FILE",
      };

    switch (fileExtension) {
      case "doc":
      case "docx":
        return {
          icon: <FileText className="file-icon word" size={24} />,
          type: fileExtension.toUpperCase(),
        };
      case "pdf":
        return {
          icon: <File className="file-icon pdf" size={24} />,
          type: "PDF",
        };
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "svg":
      case "webp":
      case "bmp":
      case "ico":
        return {
          icon: <Image className="file-icon image" size={24} />,
          type: fileExtension.toUpperCase(),
        };
      case "mp4":
      case "avi":
      case "mov":
      case "wmv":
      case "webm":
      case "mkv":
      case "flv":
        return {
          icon: <Video className="file-icon video" size={24} />,
          type: fileExtension.toUpperCase(),
        };
      case "mp3":
      case "wav":
      case "flac":
      case "aac":
      case "ogg":
      case "m4a":
        return {
          icon: <Music className="file-icon audio" size={24} />,
          type: "AUDIO",
        };
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
      case "bz2":
      case "xz":
        return {
          icon: <Archive className="file-icon archive" size={24} />,
          type: fileExtension.toUpperCase(),
        };
      case "md":
      case "markdown":
        return {
          icon: <FileCode className="file-icon markdown" size={24} />,
          type: "MARKDOWN",
        };
      case "txt":
      case "text":
        return {
          icon: <FileText className="file-icon text" size={24} />,
          type: "TEXT",
        };
      case "rtf":
        return {
          icon: <FileText className="file-icon rtf" size={24} />,
          type: "RTF",
        };
      case "json":
        return {
          icon: <Code className="file-icon json" size={24} />,
          type: "JSON",
        };
      case "xml":
      case "html":
      case "htm":
      case "css":
      case "js":
      case "ts":
      case "jsx":
      case "tsx":
        return {
          icon: <Code className="file-icon code" size={24} />,
          type: fileExtension.toUpperCase(),
        };
      case "csv":
        return {
          icon: <FileText className="file-icon csv" size={24} />,
          type: "CSV",
        };
      default:
        return {
          icon: <File className="file-icon generic" size={24} />,
          type: fileExtension.toUpperCase(),
        };
    }
  };

  const { icon, type } = getFileTypeInfo();

  // Use the shared utility function
  const isViewable = isFileViewableInBrowser(fileExtension);

  const handleDownload = () => {
    // Open the URL in a new tab for download
    window.open(url, "_blank");
  };

  const handlePreview = () => {
    setIsPreviewExpanded(!isPreviewExpanded);
  };

  const handleExpand = () => {
    dispatch(setLastViewedMessageId(message?.id || ""));

    // Navigate to AssetViewer route with search parameters
    navigate(
      `/waz/asset-viewer?title=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
    );
  };

  const handleCancelExpand = () => {
    setIsExpanded(false);
    // Reload chat by calling onCancel
    // onCancel();
  };

  const handleTogglePin = async () => {
    console.log("[DCAsset] handleTogglePin called");
    console.log("[DCAsset] storeUser:", storeUser);
    console.log("[DCAsset] message:", message);
    console.log("[DCAsset] message.pinned:", message?.pinned);
    
    if (!storeUser?.id) {
      console.log("[DCAsset] No storeUser.id found");
      return;
    }

    try {
      console.log("[DCAsset] Calling apiToggleMessagePin with userId:", storeUser.id, "messageId:", message?.id);
      const updatedMessage = await apiToggleMessagePin(storeUser.id, message?.id || 0 as number);
      console.log("[DCAsset] API response:", updatedMessage);
      
      if (updatedMessage) {
        console.log("[DCAsset] Dispatching updateSpecificMessage");
        dispatch(updateSpecificMessage(updatedMessage));
        toast.success(message?.pinned ? "Message unpinned" : "Message pinned");
      } else {
        console.log("[DCAsset] No updatedMessage returned from API");
      }
    } catch (error) {
      console.error("Error toggling pin:", error);
      toast.error("Failed to toggle pin");
    }
  };

  // Render preview content using the shared component
  const renderPreviewContent = () => {
    if (!isPreviewExpanded) return null;

    return (
      <div className="preview-content">
        <FilePreview
          url={url}
          title={title}
          fileExtension={fileExtension}
        />
      </div>
    );
  };

  useEffect(() => {
    console.log("[DCAsset] Rendering with message.pinned:", message?.pinned);
  }, [message?.pinned]);

  return (
    <div className="dc-asset">
      {/* Pin Icon */}
      <Button
        variant="tertiary"
        size="sm"
        onClick={handleTogglePin}
        title={message?.pinned ? "Unpin message" : "Pin message"}
        leftIcon={message?.pinned ? <Pin size={12} /> : <PinOff size={12} />}
      >
        {message?.pinned ? <Pin size={12} /> : <PinOff size={12} />}
      </Button>
      
      {/* <button
        className={`pin-button ${message.pinned ? 'pinned' : ''}`}
        onClick={handleTogglePin}
        title={message.pinned ? "Unpin message" : "Pin message"}
      >
        {message.pinned ? <Pin size={12} /> : <PinOff size={12} />}
      </button> */}

      <div className="asset-header">
        {url && (
          <div className="file-info">
            {icon}
            <span className="file-type-label">{type}</span>
            <div className="file-details">
              <h3 className="file-title">{title}</h3>
            </div>
          </div>
        )}
        {!url && (
          <div className="file-info">
            <h3 className="file-title">File not found</h3>
          </div>
        )}

        {url && (
          <div className="asset-actions">
            {isViewable && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePreview}
                title={isPreviewExpanded ? "Hide preview" : "Preview file"}
              >
                {isPreviewExpanded ? <EyeOff size={16} /> : <Eye size={16} />}
                <span>{isPreviewExpanded ? "Hide" : "Preview"}</span>
              </Button>

              // <button
              //   className={`action-button preview-button ${isPreviewExpanded ? "active" : ""}`}
              //   onClick={handlePreview}
              //   title={isPreviewExpanded ? "Hide preview" : "Preview file"}>
              //   {isPreviewExpanded ? <EyeOff size={16} /> : <Eye size={16} />}
              //   <span>{isPreviewExpanded ? "Hide" : "Preview"}</span>
              // </button>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleExpand}
              title="Expand to full view"
            >
              <Maximize2 size={16} />
              <span>Expand</span>
            </Button>

            {/* <button
              className="action-button expand-button"
              onClick={handleExpand}
              title="Expand to full view">
              <Maximize2 size={16} />
              <span>Expand</span>
            </button> */}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              title="Download file"
            >
              <Download size={16} />
              <span>Download</span>
            </Button>

            {/* <button
              className="action-button download-button"
              onClick={handleDownload}
              title="Download file">
              <Download size={16} />
              <span>Download</span>
            </button> */}
          </div>
        )}
      </div>

      {/* URL section below header */}
      {url && (
        <div className="url-section">
          <span className="file-url" title={url}>
            {url.length > 80 ? `${url.substring(0, 80)}...` : url}
          </span>
        </div>
      )}

      {/* Preview section */}
      {renderPreviewContent()}

      {/* Legacy content area for backward compatibility */}
      {/* <div className="asset-content">
                {preview && (
                    <DCAssetPreview 
                        title={title}
                        url={url}
                    />
                )}
            </div> */}
    </div>
  );
};
